import FormHeader from "@/components/FormHeader";
import SkeletonRow from "@/components/SkeletonRow";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/axios";
import {
  getListJenjangPendidikan,
  getListProgramStudentExchange,
} from "@/services/getData";
import { JalurJenis, Periode } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  CalendarIcon,
  Clock,
  Loader2,
  Settings2,
  Wallet,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa6";
import { MdClose, MdModeEdit } from "react-icons/md";

// --- API Functions (Tetap sama) ---
const postData = async (newData: Periode) => {
  const response = await api.post("/setting/periode/store", newData);
  return response.data;
};

const patchData = async (newData: Periode) => {
  const response = await api.patch("/setting/periode/update", newData);
  return response.data;
};

const deleteData = async (deletedData: { id_sys_periode: string }) => {
  const response = await api({
    url: "/setting/periode/delete",
    method: "DELETE",
    data: deletedData,
  });
  return response.data;
};

const getListJalurJenis = async () => {
  const response = await api.get("/master/jalur-jenis/index", {
    withCredentials: true,
  });
  return response.data?.data;
};

// --- Component Helper untuk Section ---
const FormSection = ({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="border rounded-xl p-5 bg-card text-card-foreground shadow-sm space-y-4">
    <div className="flex items-center gap-2 border-b pb-2 mb-2">
      <div className="p-1.5 bg-primary/10 rounded-md text-primary">{icon}</div>
      <h3 className="font-semibold text-lg tracking-tight">{title}</h3>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
  </div>
);

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE";
  selectedRow?: Periode | null;
  closeModal: () => void;
}) {
  const { data: dataJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  });
  const {
    data: dataJenjangPendidikan,
    isPending: isPendingDataJenjangPendidikan,
  } = useQuery({
    queryKey: ["listJenjangPendidikan"],
    queryFn: getListJenjangPendidikan,
  });
  const {
    data: dataProgramStudentExchange,
    isPending: isPendingDataProgramStudentExchange,
  } = useQuery({
    queryKey: ["listProgramStudentExchange"],
    queryFn: getListProgramStudentExchange,
  });

  const [form, setForm] = useState<any>({
    id_sys_periode: selectedRow?.id_sys_periode || "",
    id_ref_jalur_jenis: selectedRow?.id_ref_jalur_jenis
      ? String(selectedRow.id_ref_jalur_jenis)
      : "",
    tahun: selectedRow?.tahun || "",
    gelombang: selectedRow?.gelombang || "",
    tahun_ajaran_masuk: selectedRow?.tahun_ajaran_masuk || "",
    semester_masuk: selectedRow?.semester_masuk || "",
    waktu_mulai_daftar: selectedRow?.waktu_mulai_daftar || "",
    waktu_selesai_daftar: selectedRow?.waktu_selesai_daftar || "",
    waktu_selesai_daftar_valid: selectedRow?.waktu_selesai_daftar_valid || "",
    waktu_pengumuman_kelulusan: selectedRow?.waktu_pengumuman_kelulusan || "",
    waktu_mulai_isi_ukt: selectedRow?.waktu_mulai_isi_ukt || "",
    waktu_selesai_isi_ukt: selectedRow?.waktu_selesai_isi_ukt || "",
    waktu_pengumuman: selectedRow?.waktu_pengumuman || "",
    waktu_mulai_daftarkembali: selectedRow?.waktu_mulai_daftarkembali || "",
    waktu_selesai_daftarkembali: selectedRow?.waktu_selesai_daftarkembali || "",
    waktu_mulai_sanggah_berkas_daftarkembali:
      selectedRow?.waktu_mulai_sanggah_berkas_daftarkembali || "",
    waktu_selesai_sanggah_berkas_daftarkembali:
      selectedRow?.waktu_selesai_sanggah_berkas_daftarkembali || "",
    waktu_mulai_sanggah_kelulusan:
      selectedRow?.waktu_mulai_sanggah_kelulusan || "",
    waktu_selesai_sanggah_kelulusan:
      selectedRow?.waktu_selesai_sanggah_kelulusan || "",
    waktu_pengumuman_sanggah_kelulusan:
      selectedRow?.waktu_pengumuman_sanggah_kelulusan || "",
    waktu_pengumuman_kelulusan_cadangan:
      selectedRow?.waktu_pengumuman_kelulusan_cadangan || "",
    waktu_mulai_daftarkembali_cadangan:
      selectedRow?.waktu_mulai_daftarkembali_cadangan || "",
    waktu_selesai_daftarkembali_cadangan:
      selectedRow?.waktu_selesai_daftarkembali_cadangan || "",
    tanggal_mulai_kuliah: selectedRow?.tanggal_mulai_kuliah || "",
    link_telegram: selectedRow?.link_telegram || "",
    link_telegram_pkkmb: selectedRow?.link_telegram_pkkmb || "",
    jenjang_buka: selectedRow?.jenjang_buka || [],
    biaya_daftar: selectedRow?.biaya_daftar || "",
    biaya_portofolio: selectedRow?.biaya_portofolio || "",
    waktu_mulai_bayar_ukt: selectedRow?.waktu_mulai_bayar_ukt || "",
    waktu_selesai_bayar_ukt: selectedRow?.waktu_selesai_bayar_ukt || "",
    tanggal_cbt: selectedRow?.tanggal_cbt || [],
    is_cek_jurusan_sekolah: selectedRow?.is_cek_jurusan_sekolah ?? false,
    waktu_tkb_pasca: selectedRow?.waktu_tkb_pasca || "",
    waktu_tpa_pasca: selectedRow?.waktu_tpa_pasca || "",
    daftar_program:
      selectedRow?.sys_daftar_program?.map((sys: any) => {
        return { id_ref_daftar_program: sys.id_ref_daftar_program };
      }) || [],
  });

  const [countTanggalCbt, setCountTanggalCbt] = useState<number>(
    selectedRow?.tanggal_cbt?.length || 1,
  );

  const isPost = method === "POST";
  const isPatch = method === "PATCH";

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["listPeriode"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: any) => {
      const err = error;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  const selectedJalurJenis: any | undefined = dataJalurJenis?.find(
    (item: JalurJenis) => {
      return item.id_ref_jalur_jenis == form.id_ref_jalur_jenis;
    },
  );

  const isJalurStudentExchange =
    selectedJalurJenis?.nama_jalur_jenis_slug == "studentexchange";

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-6">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Tambah Data Periode" : "Ubah Data Periode"}
      />

      {/* Bagian 1: Informasi Dasar */}
      <FormSection title="Informasi Dasar" icon={<Settings2 size={18} />}>
        <div className="md:col-span-2">
          <Label required>Jenis Jalur Penerimaan</Label>
          <Select
            required
            value={form.id_ref_jalur_jenis}
            onValueChange={(value) => {
              if (!value) return;
              setForm((prev: any) => ({ ...prev, id_ref_jalur_jenis: value }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={"Pilih Jenis Jalur"} />
            </SelectTrigger>
            <SelectContent>
              {dataJalurJenis?.map((jalurJenis: any) => (
                <SelectItem
                  key={jalurJenis.id_ref_jalur_jenis}
                  value={`${jalurJenis.id_ref_jalur_jenis}`}
                >
                  {jalurJenis.nama_jalur_jenis}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label required>Tahun Periode</Label>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={4}
            required
            placeholder="Contoh: 2024"
            value={form.tahun}
            onChange={(event: any) =>
              setForm((prev: any) => ({ ...prev, tahun: event.target.value.replace(/\D/g, '') }))
            }
          />
        </div>

        <div>
          <Label required>Gelombang</Label>
          <Input
            type="text"
            inputMode="numeric"
            required
            placeholder="Contoh: 1"
            value={form.gelombang}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                gelombang: event.target.value.replace(/\D/g, ''),
              }))
            }
          />
        </div>

        <div>
          <Label required>Tahun Ajaran Masuk</Label>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={4}
            required
            placeholder="Contoh: 2024"
            value={form.tahun_ajaran_masuk}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                tahun_ajaran_masuk: event.target.value.replace(/\D/g, ''),
              }))
            }
          />
        </div>

        <div>
          <Label required>Semester Masuk</Label>
          <Input
            type="text"
            inputMode="numeric"
            required
            maxLength={1}
            placeholder="Contoh: 1"
            value={form.semester_masuk}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                semester_masuk: event.target.value.replace(/\D/g, ''),
              }))
            }
          />
        </div>
      </FormSection>

      {/* Bagian 2: Jadwal Pendaftaran */}
      <FormSection title="Jadwal Pendaftaran" icon={<CalendarIcon size={18} />}>
        <div>
          <Label required>Waktu Mulai Daftar</Label>
          <Input
            required
            type="datetime-local"
            value={form.waktu_mulai_daftar}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_mulai_daftar: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label required>Waktu Selesai Daftar</Label>
          <Input
            required
            type="datetime-local"
            value={form.waktu_selesai_daftar}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_selesai_daftar: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label required>Waktu Selesai Daftar Valid</Label>
          <Input
            required
            type="datetime-local"
            value={form.waktu_selesai_daftar_valid}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_selesai_daftar_valid: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label required>Waktu Pengumuman Kelulusan</Label>
          <Input
            required
            type="datetime-local"
            value={form.waktu_pengumuman_kelulusan}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_pengumuman_kelulusan: event.target.value,
              }))
            }
          />
        </div>
      </FormSection>

      {/* Bagian 3: Keuangan & Tautan */}
      <FormSection title="Keuangan & Tautan" icon={<Wallet size={18} />}>
        <div>
          <Label>Biaya Daftar (Rp)</Label>
          <Input
            placeholder="0"
            type="number"
            value={form.biaya_daftar}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                biaya_daftar: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label>Biaya Portofolio (Rp)</Label>
          <Input
            placeholder="0"
            type="number"
            value={form.biaya_portofolio}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                biaya_portofolio: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label required={!isJalurStudentExchange}>Link Telegram</Label>
          <Input
            required={!isJalurStudentExchange}
            placeholder="https://t.me/..."
            value={form.link_telegram}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                link_telegram: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label required={!isJalurStudentExchange}>Link Telegram PKKMB</Label>
          <Input
            required={!isJalurStudentExchange}
            placeholder="https://t.me/..."
            value={form.link_telegram_pkkmb}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                link_telegram_pkkmb: event.target.value,
              }))
            }
          />
        </div>
      </FormSection>

      {/* Bagian 4: UKT & Daftar Kembali */}
      <FormSection
        title="Jadwal UKT & Daftar Kembali"
        icon={<Clock size={18} />}
      >
        <div>
          <Label required={!isJalurStudentExchange}>Waktu Mulai Isi UKT</Label>
          <Input
            required={!isJalurStudentExchange}
            type="datetime-local"
            value={form.waktu_mulai_isi_ukt}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_mulai_isi_ukt: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label required={!isJalurStudentExchange}>
            Waktu Selesai Isi UKT
          </Label>
          <Input
            required={!isJalurStudentExchange}
            type="datetime-local"
            value={form.waktu_selesai_isi_ukt}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_selesai_isi_ukt: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label required={!isJalurStudentExchange}>Waktu Pengumuman UKT</Label>
          <Input
            required={!isJalurStudentExchange}
            type="datetime-local"
            value={form.waktu_pengumuman}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_pengumuman: event.target.value,
              }))
            }
          />
        </div>

        <div className="hidden md:block"></div>

        <div>
          <Label required={!isJalurStudentExchange}>
            Waktu Mulai Bayar UKT
          </Label>
          <Input
            required={!isJalurStudentExchange}
            type="datetime-local"
            value={form.waktu_mulai_bayar_ukt}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_mulai_bayar_ukt: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label required={!isJalurStudentExchange}>
            Waktu Selesai Bayar UKT
          </Label>
          <Input
            required={!isJalurStudentExchange}
            type="datetime-local"
            value={form.waktu_selesai_bayar_ukt}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_selesai_bayar_ukt: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label required={!isJalurStudentExchange}>
            Waktu Mulai Daftar Kembali (Cetak NIM)
          </Label>
          <Input
            required={!isJalurStudentExchange}
            type="datetime-local"
            value={form.waktu_mulai_daftarkembali}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_mulai_daftarkembali: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label required={!isJalurStudentExchange}>
            Waktu Selesai Daftar Kembali (Cetak NIM)
          </Label>
          <Input
            required={!isJalurStudentExchange}
            type="datetime-local"
            value={form.waktu_selesai_daftarkembali}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_selesai_daftarkembali: event.target.value,
              }))
            }
          />
        </div>
        <div className="md:col-span-2">
          <Label required={!isJalurStudentExchange}>Tanggal Mulai Kuliah</Label>
          <Input
            required={!isJalurStudentExchange}
            type="date"
            value={
              form.tanggal_mulai_kuliah
                ? form.tanggal_mulai_kuliah.split(" ")[0]
                : ""
            }
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                tanggal_mulai_kuliah: event.target.value,
              }))
            }
          />
        </div>
      </FormSection>

      {/* Bagian 5: Masa Sanggah */}
      <FormSection
        title="Masa Sanggah & Cadangan"
        icon={<Settings2 size={18} />}
      >
        <div>
          <Label>Waktu Mulai Sanggah Kelulusan</Label>
          <Input
            type="datetime-local"
            value={form.waktu_mulai_sanggah_kelulusan}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_mulai_sanggah_kelulusan: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label>Waktu Selesai Sanggah Kelulusan</Label>
          <Input
            type="datetime-local"
            value={form.waktu_selesai_sanggah_kelulusan}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_selesai_sanggah_kelulusan: event.target.value,
              }))
            }
          />
        </div>
        {/* <div>
          <Label>Waktu Pengumuman Sanggah</Label>
          <Input
            type="datetime-local"
            value={form.waktu_pengumuman_sanggah_kelulusan}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_pengumuman_sanggah_kelulusan: event.target.value,
              }))
            }
          />
        </div> */}
        {/* <div className="hidden md:block"></div>
        <div className="hidden md:block"></div> */}

        <div>
          <Label required={!isJalurStudentExchange}>
            Mulai Sanggah Berkas Daftar Kembali
          </Label>
          <Input
            required={!isJalurStudentExchange}
            type="datetime-local"
            value={form.waktu_mulai_sanggah_berkas_daftarkembali}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_mulai_sanggah_berkas_daftarkembali: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label required={!isJalurStudentExchange}>
            Selesai Sanggah Berkas Daftar Kembali
          </Label>
          <Input
            required={!isJalurStudentExchange}
            type="datetime-local"
            value={form.waktu_selesai_sanggah_berkas_daftarkembali}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_selesai_sanggah_berkas_daftarkembali: event.target.value,
              }))
            }
          />
        </div>

        {/* <div>
          <Label>Pengumuman Kelulusan Cadangan</Label>
          <Input
            type="datetime-local"
            value={form.waktu_pengumuman_kelulusan_cadangan}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_pengumuman_kelulusan_cadangan: event.target.value,
              }))
            }
          />
        </div>
        <div className="hidden md:block"></div>
        <div>
          <Label>Mulai Daftar Kembali Cadangan</Label>
          <Input
            type="datetime-local"
            value={form.waktu_mulai_daftarkembali_cadangan}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_mulai_daftarkembali_cadangan: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label>Selesai Daftar Kembali Cadangan</Label>
          <Input
            type="datetime-local"
            value={form.waktu_selesai_daftarkembali_cadangan}
            onChange={(event: any) =>
              setForm((prev: any) => ({
                ...prev,
                waktu_selesai_daftarkembali_cadangan: event.target.value,
              }))
            }
          />
        </div> */}
      </FormSection>

      {/* Bagian 6: Konfigurasi Khusus */}
      {selectedJalurJenis?.is_cbt_internal ||
        selectedJalurJenis?.nama_jalur_jenis_slug === "pascasarjana" ||
        form.is_cek_jurusan_sekolah !== undefined ? (
        <FormSection title="Konfigurasi Khusus" icon={<Settings2 size={18} />}>
          {selectedJalurJenis && selectedJalurJenis.is_cbt_internal ? (
            <div className="md:col-span-2 space-y-3 p-4 border border-dashed rounded-lg">
              <h4 className="font-medium text-sm text-muted-foreground">
                Jadwal CBT
              </h4>
              {Array.from({ length: countTanggalCbt }).map((_, index) => (
                <div key={index} className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label required>Tanggal CBT {index + 1}</Label>
                    <Input
                      required
                      type="date"
                      value={form.tanggal_cbt ? form.tanggal_cbt[index] : ""}
                      onChange={(event: any) =>
                        setForm((prev: any) => {
                          const updatedTanggal = [...(prev.tanggal_cbt || [])];
                          updatedTanggal[index] = event.target.value;
                          return { ...prev, tanggal_cbt: updatedTanggal };
                        })
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    size={"icon"}
                    variant={"outline"}
                    className="hover:bg-destructive hover:text-destructive-foreground transition-colors"
                    onClick={() => {
                      setForm((prev: any) => {
                        const updatedTanggal = (prev.tanggal_cbt || []).filter(
                          (_: any, i: any) => i !== index,
                        );
                        return { ...prev, tanggal_cbt: updatedTanggal };
                      });
                      setCountTanggalCbt((prev) => prev - 1);
                    }}
                  >
                    <MdClose />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-dashed"
                onClick={() => {
                  setForm((prev: any) => ({
                    ...prev,
                    tanggal_cbt: [...(prev.tanggal_cbt || []), ""],
                  }));
                  setCountTanggalCbt((prev) => prev + 1);
                }}
              >
                <FaPlus className="mr-2" /> Tambah Tanggal CBT
              </Button>
            </div>
          ) : (
            ""
          )}

          {selectedJalurJenis?.nama_jalur_jenis_slug === "pascasarjana" && (
            <>
              <div>
                <Label required>Waktu TPA Pasca</Label>
                <Input
                  required
                  type="datetime-local"
                  value={form.waktu_tpa_pasca}
                  onChange={(event: any) =>
                    setForm((prev: any) => ({
                      ...prev,
                      waktu_tpa_pasca: event.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <Label required>Waktu TKB Pasca</Label>
                <Input
                  required
                  type="datetime-local"
                  value={form.waktu_tkb_pasca}
                  onChange={(event: any) =>
                    setForm((prev: any) => ({
                      ...prev,
                      waktu_tkb_pasca: event.target.value,
                    }))
                  }
                />
              </div>
            </>
          )}

          <div className="md:col-span-2 flex items-center space-x-2 border p-3 rounded-md bg-accent/20">
            <Switch
              id="status"
              checked={form.is_cek_jurusan_sekolah}
              onCheckedChange={() =>
                setForm((prev: any) => ({
                  ...prev,
                  is_cek_jurusan_sekolah: !prev.is_cek_jurusan_sekolah,
                }))
              }
            />
            <Label htmlFor="status" className="cursor-pointer">
              Aktifkan Cek Jurusan Sekolah
            </Label>
          </div>
        </FormSection>
      ) : (
        ""
      )}

      {/* Bagian 7: Filter Program & Jenjang */}
      <FormSection
        title="Filter Program & Jenjang"
        icon={<BookOpen size={18} />}
      >
        {isJalurStudentExchange && (
          <div className="md:col-span-2">
            <Label className="mb-2 block" required={isJalurStudentExchange}>
              Daftar Program (Checklist)
            </Label>
            <div className="p-4 border rounded-lg max-h-60 overflow-y-auto bg-background">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {isPendingDataProgramStudentExchange
                  ? Array(6)
                    .fill(null)
                    .map((_, i) => <SkeletonRow key={i} />)
                  : dataProgramStudentExchange?.map((program: any) => (
                    <div
                      key={program.id_ref_daftar_program}
                      className="flex items-center space-x-2 hover:bg-accent/50 p-1.5 rounded-md transition-colors"
                    >
                      <Checkbox
                        id={`prog-${program.id_ref_daftar_program}`}
                        checked={form.daftar_program.some(
                          (item: any) =>
                            item.id_ref_daftar_program ===
                            program.id_ref_daftar_program,
                        )}
                        onCheckedChange={() => {
                          setForm((prevForm: any) => {
                            const selected = prevForm.daftar_program;
                            const isChecked = selected.some(
                              (item: any) =>
                                item.id_ref_daftar_program ===
                                program.id_ref_daftar_program,
                            );
                            const updated = isChecked
                              ? selected.filter(
                                (item: any) =>
                                  item.id_ref_daftar_program !==
                                  program.id_ref_daftar_program,
                              )
                              : [
                                ...selected,
                                {
                                  id_ref_daftar_program:
                                    program.id_ref_daftar_program,
                                },
                              ];
                            return { ...prevForm, daftar_program: updated };
                          });
                        }}
                      />
                      <Label
                        htmlFor={`prog-${program.id_ref_daftar_program}`}
                        className="font-normal cursor-pointer"
                      >
                        {program.nama_program}
                      </Label>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        <div className="md:col-span-2">
          <Label className="mb-2 block">Jenjang Pendidikan Dibuka (Checklist)</Label>
          <div className="p-4 border rounded-lg max-h-60 overflow-y-auto bg-background">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {isPendingDataJenjangPendidikan
                ? Array(6)
                  .fill(null)
                  .map((_, i) => <SkeletonRow key={i} />)
                : dataJenjangPendidikan
                  ?.filter((jenjang: any) => [20, 21, 22, 23, 30, 31, 35, 40].includes(Number(jenjang.id_feeder)))
                  .map((jenjang: any) => (
                    <div
                      key={jenjang.id_ref_jenjang_pendidikan}
                      className="flex items-center space-x-2 hover:bg-accent/50 p-1.5 rounded-md transition-colors"
                    >
                      <Checkbox
                        id={`jenjang-${jenjang.id_ref_jenjang_pendidikan}`}
                        checked={form.jenjang_buka?.some(
                          (j: any) =>
                            j.id_ref_jenjang_pendidikan ===
                            jenjang.id_ref_jenjang_pendidikan,
                        )}
                        onCheckedChange={() => {
                          setForm((prevForm: any) => {
                            const selected = prevForm.jenjang_buka || [];
                            const isChecked = selected.some(
                              (j: any) =>
                                j.id_ref_jenjang_pendidikan ==
                                jenjang.id_ref_jenjang_pendidikan,
                            );
                            const updated = isChecked
                              ? selected.filter(
                                (j: any) =>
                                  j.id_ref_jenjang_pendidikan !==
                                  jenjang.id_ref_jenjang_pendidikan,
                              )
                              : [...selected, jenjang];
                            return { ...prevForm, jenjang_buka: updated };
                          });
                        }}
                      />
                      <Label
                        htmlFor={`jenjang-${jenjang.id_ref_jenjang_pendidikan}`}
                        className="font-normal cursor-pointer"
                      >
                        {jenjang.nama_jenjang_pendidikan}
                      </Label>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </FormSection>

      {/* Action Buttons */}
      <div className="sticky bottom-0 bg-background/80 backdrop-blur-sm p-4 border-t flex justify-end gap-3 rounded-b-xl z-10">
        <Button type="button" variant={"outline"} onClick={() => closeModal()}>
          Batal
        </Button>
        <Button
          className="flex items-center gap-2 min-w-[120px]"
          disabled={isPending}
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            "Simpan"
          )}
        </Button>
      </div>
    </form>
  );
}

// export default function Form({
//   method,
//   selectedRow,
//   closeModal,
// }: {
//   method: "POST" | "PATCH" | "DELETE";
//   selectedRow?: Periode | null;
//   closeModal: () => void;
// }) {
//   const { data: dataJalurJenis } = useQuery({
//     queryKey: ["listJalurJenis"],
//     queryFn: getListJalurJenis,
//   });
//   const {
//     data: dataJenjangPendidikan,
//     isPending: isPendingDataJenjangPendidikan,
//   } = useQuery({
//     queryKey: ["listJenjangPendidikan"],
//     queryFn: getListJenjangPendidikan,
//   });
//   const {
//     data: dataProgramStudentExchange,
//     isPending: isPendingDataProgramStudentExchange,
//   } = useQuery({
//     queryKey: ["listProgramStudentExchange"],
//     queryFn: getListProgramStudentExchange,
//   });

//   const [form, setForm] = useState<
//     Periode & { daftar_program: { id_ref_daftar_program: string }[] }
//   >({
//     id_sys_periode: selectedRow?.id_sys_periode || "",
//     id_ref_jalur_jenis: selectedRow?.id_ref_jalur_jenis
//       ? String(selectedRow.id_ref_jalur_jenis)
//       : "",
//     tahun: selectedRow?.tahun || "",
//     gelombang: selectedRow?.gelombang || "",
//     tahun_ajaran_masuk: selectedRow?.tahun_ajaran_masuk || "",
//     semester_masuk: selectedRow?.semester_masuk || "",
//     waktu_mulai_daftar: selectedRow?.waktu_mulai_daftar || "",
//     waktu_selesai_daftar: selectedRow?.waktu_selesai_daftar || "",
//     waktu_selesai_daftar_valid: selectedRow?.waktu_selesai_daftar_valid || "",
//     waktu_pengumuman_kelulusan: selectedRow?.waktu_pengumuman_kelulusan || "",
//     waktu_mulai_isi_ukt: selectedRow?.waktu_mulai_isi_ukt || "",
//     waktu_selesai_isi_ukt: selectedRow?.waktu_selesai_isi_ukt || "",
//     waktu_pengumuman: selectedRow?.waktu_pengumuman || "",
//     waktu_mulai_daftarkembali: selectedRow?.waktu_mulai_daftarkembali || "",
//     waktu_selesai_daftarkembali: selectedRow?.waktu_selesai_daftarkembali || "",
//     waktu_mulai_sanggah_berkas_daftarkembali:
//       selectedRow?.waktu_mulai_sanggah_berkas_daftarkembali || "",
//     waktu_selesai_sanggah_berkas_daftarkembali:
//       selectedRow?.waktu_selesai_sanggah_berkas_daftarkembali || "",
//     waktu_mulai_sanggah_kelulusan:
//       selectedRow?.waktu_mulai_sanggah_kelulusan || "",
//     waktu_selesai_sanggah_kelulusan:
//       selectedRow?.waktu_selesai_sanggah_kelulusan || "",
//     waktu_pengumuman_sanggah_kelulusan:
//       selectedRow?.waktu_pengumuman_sanggah_kelulusan || "",
//     waktu_pengumuman_kelulusan_cadangan:
//       selectedRow?.waktu_pengumuman_kelulusan_cadangan || "",
//     waktu_mulai_daftarkembali_cadangan:
//       selectedRow?.waktu_mulai_daftarkembali_cadangan || "",
//     waktu_selesai_daftarkembali_cadangan:
//       selectedRow?.waktu_selesai_daftarkembali_cadangan || "",
//     tanggal_mulai_kuliah: selectedRow?.tanggal_mulai_kuliah || "",
//     link_telegram: selectedRow?.link_telegram || "",
//     link_telegram_pkkmb: selectedRow?.link_telegram_pkkmb || "",
//     jenjang_buka: selectedRow?.jenjang_buka || [],
//     biaya_daftar: selectedRow?.biaya_daftar || "",
//     biaya_portofolio: selectedRow?.biaya_portofolio || "",
//     waktu_mulai_bayar_ukt: selectedRow?.waktu_mulai_bayar_ukt || "",
//     waktu_selesai_bayar_ukt: selectedRow?.waktu_selesai_bayar_ukt || "",
//     tanggal_cbt: selectedRow?.tanggal_cbt || [],
//     is_cek_jurusan_sekolah: selectedRow?.is_cek_jurusan_sekolah ?? false,
//     waktu_tkb_pasca: selectedRow?.waktu_tkb_pasca || "",
//     waktu_tpa_pasca: selectedRow?.waktu_tpa_pasca || "",
//     daftar_program:
//       selectedRow?.sys_daftar_program?.map((sys) => {
//         return { id_ref_daftar_program: sys.id_ref_daftar_program };
//       }) || [],
//   });
//   const [countTanggalCbt, setCountTanggalCbt] = useState(
//     selectedRow?.tanggal_cbt?.length || 1,
//   );

//   const isPost = method === "POST";
//   const isPatch = method === "PATCH";

//   const queryClient = useQueryClient();
//   const { mutate, isPending } = useMutation({
//     mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
//     onSuccess: (data) => {
//       queryClient.invalidateQueries({ queryKey: ["listPeriode"] });
//       closeModal();
//       toast.success(data?.message || "Success.");
//     },
//     onError: (error: AxiosError) => {
//       const err = error as AxiosError<{ message?: string }>;
//       toast.error(
//         err?.response?.data?.message || "An unexpected error occurred.",
//       );
//     },
//   });

//   const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
//     event.preventDefault();
//     mutate(form);
//   };

//   const selectedJalurJenis: JalurJenis | undefined = dataJalurJenis?.find(
//     (item: JalurJenis) => {
//       return item.id_ref_jalur_jenis == form.id_ref_jalur_jenis;
//     },
//   );

//   const isJalurStudentExchange =
//     selectedJalurJenis?.nama_jalur_jenis_slug == "studentexchange";

//   return (
//     <form onSubmit={handleSubmit} className="flex flex-col gap-y-6">
//       <FormHeader
//         icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
//         title={isPost ? "Tambah Data Periode" : "Ubah Data Periode"}
//       />

//       {/* Bagian 1: Informasi Dasar */}
//       <FormSection title="Informasi Dasar" icon={<Settings2 size={18} />}>
//         <div className="md:col-span-2">
//           <Label required>Ref Jalur Jenis</Label>
//           <Select
//             required
//             value={form.id_ref_jalur_jenis}
//             onValueChange={(value) => {
//               if (!value) return;
//               setForm((prev) => ({ ...prev, id_ref_jalur_jenis: value }));
//             }}
//           >
//             <SelectTrigger>
//               <SelectValue placeholder={"Pilih Ref Jalur"} />
//             </SelectTrigger>
//             <SelectContent>
//               {dataJalurJenis?.map((jalurJenis: JalurJenis) => (
//                 <SelectItem
//                   key={jalurJenis.id_ref_jalur_jenis}
//                   value={`${jalurJenis.id_ref_jalur_jenis}`}
//                 >
//                   {jalurJenis.nama_jalur_jenis}
//                 </SelectItem>
//               ))}
//             </SelectContent>
//           </Select>
//         </div>

//         <div>
//           <Label required>Tahun</Label>
//           <Input
//             maxLength={4}
//             required
//             placeholder="Contoh: 2024"
//             value={form.tahun}
//             onChange={(event) =>
//               setForm((prev) => ({ ...prev, tahun: event.target.value }))
//             }
//           />
//         </div>

//         <div>
//           <Label required>Gelombang</Label>
//           <Input
//             required
//             placeholder="Contoh: 1"
//             value={form.gelombang}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 gelombang: event.target.value,
//               }))
//             }
//           />
//         </div>

//         <div>
//           <Label required>Tahun Ajaran Masuk</Label>
//           <Input
//             maxLength={4}
//             required
//             placeholder="Contoh: 2024"
//             value={form.tahun_ajaran_masuk}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 tahun_ajaran_masuk: event.target.value,
//               }))
//             }
//           />
//         </div>

//         <div>
//           <Label required>Semester Masuk</Label>
//           <Input
//             required
//             maxLength={1}
//             placeholder="Contoh: 1"
//             value={form.semester_masuk}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 semester_masuk: event.target.value,
//               }))
//             }
//           />
//         </div>
//       </FormSection>

//       {/* Bagian 2: Jadwal Pendaftaran */}
//       <FormSection title="Jadwal Pendaftaran" icon={<CalendarIcon size={18} />}>
//         <div>
//           <Label>Waktu Mulai Daftar</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_mulai_daftar}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_mulai_daftar: event.target.value,
//               }))
//             }
//           />
//         </div>

//         <div>
//           <Label>Waktu Selesai Daftar</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_selesai_daftar}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_selesai_daftar: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div>
//           <Label>Waktu Selesai Validasi Pendaftaran</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_selesai_daftar_valid}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_selesai_daftar_valid: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div>
//           <Label required>Waktu Pengumuman Kelulusan</Label>
//           <Input
//             required
//             type="datetime-local"
//             value={form.waktu_pengumuman_kelulusan}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_pengumuman_kelulusan: event.target.value,
//               }))
//             }
//           />
//         </div>
//       </FormSection>

//       {/* Bagian 3: Keuangan & Tautan */}
//       <FormSection title="Keuangan & Tautan" icon={<Wallet size={18} />}>
//         <div>
//           <Label>Biaya Daftar (Rp)</Label>
//           <Input
//             placeholder="0"
//             type="number"
//             value={form.biaya_daftar}
//             onChange={(event) =>
//               setForm((prev) => ({ ...prev, biaya_daftar: event.target.value }))
//             }
//           />
//         </div>
//         <div>
//           <Label>Biaya Portofolio (Rp)</Label>
//           <Input
//             placeholder="0"
//             type="number"
//             value={form.biaya_portofolio}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 biaya_portofolio: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div>
//           <Label required>Link Telegram</Label>
//           <Input
//             required
//             placeholder="https://t.me/..."
//             value={form.link_telegram}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 link_telegram: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div>
//           <Label required>Link Telegram PKKMB</Label>
//           <Input
//             required
//             placeholder="https://t.me/..."
//             value={form.link_telegram_pkkmb}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 link_telegram_pkkmb: event.target.value,
//               }))
//             }
//           />
//         </div>
//       </FormSection>

//       {/* Bagian 4: UKT & Daftar Kembali */}
//       <FormSection
//         title="Jadwal UKT & Daftar Kembali"
//         icon={<Clock size={18} />}
//       >
//         <div>
//           <Label>Waktu Mulai Isi UKT</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_mulai_isi_ukt}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_mulai_isi_ukt: event.target.value,
//               }))
//             }
//           />
//         </div>

//         <div>
//           <Label>Waktu Selesai Isi UKT</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_selesai_isi_ukt}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_selesai_isi_ukt: event.target.value,
//               }))
//             }
//           />
//         </div>

//         <div>
//           <Label required>Waktu Pengumuman UKT</Label>
//           <Input
//             required
//             type="datetime-local"
//             value={form.waktu_pengumuman}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_pengumuman: event.target.value,
//               }))
//             }
//           />
//         </div>

//         <div className="hidden md:block"></div>

//         <div>
//           <Label>Waktu Mulai Bayar UKT</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_mulai_bayar_ukt}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_mulai_bayar_ukt: event.target.value,
//               }))
//             }
//           />
//         </div>

//         <div>
//           <Label>Waktu Selesai Bayar UKT</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_selesai_bayar_ukt}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_selesai_bayar_ukt: event.target.value,
//               }))
//             }
//           />
//         </div>

//         <div>
//           <Label required>Waktu Mulai Daftar Kembali</Label>
//           <Input
//             required
//             type="datetime-local"
//             value={form.waktu_mulai_daftarkembali}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_mulai_daftarkembali: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div>
//           <Label required>Waktu Selesai Daftar Kembali</Label>
//           <Input
//             required
//             type="datetime-local"
//             value={form.waktu_selesai_daftarkembali}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_selesai_daftarkembali: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div className="md:col-span-2">
//           <Label required>Tanggal Mulai Kuliah</Label>
//           <Input
//             required
//             type="date"
//             value={
//               form.tanggal_mulai_kuliah
//                 ? form.tanggal_mulai_kuliah.split(" ")[0]
//                 : ""
//             }
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 tanggal_mulai_kuliah: event.target.value,
//               }))
//             }
//           />
//         </div>
//       </FormSection>

//       {/* Bagian 5: Masa Sanggah */}
//       <FormSection
//         title="Masa Sanggah & Cadangan"
//         icon={<Settings2 size={18} />}
//       >
//         <div>
//           <Label>Waktu Mulai Sanggah Kelulusan</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_mulai_sanggah_kelulusan}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_mulai_sanggah_kelulusan: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div>
//           <Label>Waktu Selesai Sanggah Kelulusan</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_selesai_sanggah_kelulusan}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_selesai_sanggah_kelulusan: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div>
//           <Label>Waktu Pengumuman Sanggah</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_pengumuman_sanggah_kelulusan}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_pengumuman_sanggah_kelulusan: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div className="hidden md:block"></div>

//         <div>
//           <Label required>Mulai Sanggah Berkas Daftar Kembali</Label>
//           <Input
//             required
//             type="datetime-local"
//             value={form.waktu_mulai_sanggah_berkas_daftarkembali}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_mulai_sanggah_berkas_daftarkembali: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div>
//           <Label required>Selesai Sanggah Berkas Daftar Kembali</Label>
//           <Input
//             required
//             type="datetime-local"
//             value={form.waktu_selesai_sanggah_berkas_daftarkembali}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_selesai_sanggah_berkas_daftarkembali: event.target.value,
//               }))
//             }
//           />
//         </div>

//         <div>
//           <Label>Pengumuman Kelulusan Cadangan</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_pengumuman_kelulusan_cadangan}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_pengumuman_kelulusan_cadangan: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div className="hidden md:block"></div>
//         <div>
//           <Label>Mulai Daftar Kembali Cadangan</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_mulai_daftarkembali_cadangan}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_mulai_daftarkembali_cadangan: event.target.value,
//               }))
//             }
//           />
//         </div>
//         <div>
//           <Label>Selesai Daftar Kembali Cadangan</Label>
//           <Input
//             type="datetime-local"
//             value={form.waktu_selesai_daftarkembali_cadangan}
//             onChange={(event) =>
//               setForm((prev) => ({
//                 ...prev,
//                 waktu_selesai_daftarkembali_cadangan: event.target.value,
//               }))
//             }
//           />
//         </div>
//       </FormSection>

//       {/* Bagian 6: Konfigurasi Khusus */}
//       {selectedJalurJenis?.is_cbt_internal ||
//       selectedJalurJenis?.nama_jalur_jenis_slug === "pascasarjana" ||
//       form.is_cek_jurusan_sekolah !== undefined ? (
//         <FormSection title="Konfigurasi Khusus" icon={<Settings2 size={18} />}>
//           {selectedJalurJenis && selectedJalurJenis.is_cbt_internal ? (
//             <div className="md:col-span-2 space-y-3 p-4 border border-dashed rounded-lg">
//               <h4 className="font-medium text-sm text-muted-foreground">
//                 Jadwal CBT
//               </h4>
//               {Array.from({ length: countTanggalCbt }).map((_, index) => (
//                 <div key={index} className="flex items-end gap-3">
//                   <div className="flex-1">
//                     <Label required>Tanggal CBT {index + 1}</Label>
//                     <Input
//                       required
//                       type="date"
//                       value={form.tanggal_cbt ? form.tanggal_cbt[index] : ""}
//                       onChange={(event) =>
//                         setForm((prev) => {
//                           const updatedTanggal = [...(prev.tanggal_cbt || [])];
//                           updatedTanggal[index] = event.target.value;
//                           return { ...prev, tanggal_cbt: updatedTanggal };
//                         })
//                       }
//                     />
//                   </div>
//                   <Button
//                     type="button"
//                     size={"icon"}
//                     variant={"outline"}
//                     className="hover:bg-destructive hover:text-destructive-foreground transition-colors"
//                     onClick={() => {
//                       setForm((prev) => {
//                         const updatedTanggal = (prev.tanggal_cbt || []).filter(
//                           (_, i) => i !== index,
//                         );
//                         return { ...prev, tanggal_cbt: updatedTanggal };
//                       });
//                       setCountTanggalCbt((prev) => prev - 1);
//                     }}
//                   >
//                     <MdClose />
//                   </Button>
//                 </div>
//               ))}
//               <Button
//                 type="button"
//                 variant="outline"
//                 size="sm"
//                 className="w-full border-dashed"
//                 onClick={() => {
//                   setForm((prev) => ({
//                     ...prev,
//                     tanggal_cbt: [...(prev.tanggal_cbt || []), ""],
//                   }));
//                   setCountTanggalCbt((prev) => prev + 1);
//                 }}
//               >
//                 <FaPlus className="mr-2" /> Tambah Tanggal CBT
//               </Button>
//             </div>
//           ) : (
//             ""
//           )}

//           {selectedJalurJenis?.nama_jalur_jenis_slug === "pascasarjana" && (
//             <>
//               <div>
//                 <Label required>Waktu TPA Pasca</Label>
//                 <Input
//                   required
//                   type="datetime-local"
//                   value={form.waktu_tpa_pasca}
//                   onChange={(event) =>
//                     setForm((prev) => ({
//                       ...prev,
//                       waktu_tpa_pasca: event.target.value,
//                     }))
//                   }
//                 />
//               </div>
//               <div>
//                 <Label required>Waktu TKB Pasca</Label>
//                 <Input
//                   required
//                   type="datetime-local"
//                   value={form.waktu_tkb_pasca}
//                   onChange={(event) =>
//                     setForm((prev) => ({
//                       ...prev,
//                       waktu_tkb_pasca: event.target.value,
//                     }))
//                   }
//                 />
//               </div>
//             </>
//           )}

//           <div className="md:col-span-2 flex items-center space-x-2 border p-3 rounded-md bg-accent/20">
//             <Switch
//               id="status"
//               checked={form.is_cek_jurusan_sekolah}
//               onCheckedChange={() =>
//                 setForm((prev) => ({
//                   ...prev,
//                   is_cek_jurusan_sekolah: !prev.is_cek_jurusan_sekolah,
//                 }))
//               }
//             />
//             <Label htmlFor="status" className="cursor-pointer">
//               Aktifkan Cek Jurusan Sekolah
//             </Label>
//           </div>
//         </FormSection>
//       ) : (
//         ""
//       )}

//       {/* Bagian 7: Filter Program & Jenjang */}
//       <FormSection
//         title="Filter Program & Jenjang"
//         icon={<BookOpen size={18} />}
//       >
//         {isJalurStudentExchange && (
//           <div className="md:col-span-2">
//             <Label className="mb-2 block">Daftar Program (Checklist)</Label>
//             <div className="p-4 border rounded-lg max-h-60 overflow-y-auto bg-background">
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
//                 {isPendingDataProgramStudentExchange
//                   ? Array(6)
//                       .fill(null)
//                       .map((_, i) => <SkeletonRow key={i} />)
//                   : dataProgramStudentExchange?.map(
//                       (program: RefDaftarProgram) => (
//                         <div
//                           key={program.id_ref_daftar_program}
//                           className="flex items-center space-x-2 hover:bg-accent/50 p-1.5 rounded-md transition-colors"
//                         >
//                           <Checkbox
//                             id={`prog-${program.id_ref_daftar_program}`}
//                             checked={form.daftar_program.some(
//                               (item) =>
//                                 item.id_ref_daftar_program ===
//                                 program.id_ref_daftar_program,
//                             )}
//                             onCheckedChange={() => {
//                               setForm((prevForm) => {
//                                 const selected = prevForm.daftar_program;
//                                 const isChecked = selected.some(
//                                   (item) =>
//                                     item.id_ref_daftar_program ===
//                                     program.id_ref_daftar_program,
//                                 );
//                                 const updated = isChecked
//                                   ? selected.filter(
//                                       (item) =>
//                                         item.id_ref_daftar_program !==
//                                         program.id_ref_daftar_program,
//                                     )
//                                   : [
//                                       ...selected,
//                                       {
//                                         id_ref_daftar_program:
//                                           program.id_ref_daftar_program,
//                                       },
//                                     ];
//                                 return { ...prevForm, daftar_program: updated };
//                               });
//                             }}
//                           />
//                           <Label
//                             htmlFor={`prog-${program.id_ref_daftar_program}`}
//                             className="font-normal cursor-pointer"
//                           >
//                             {program.nama_program}
//                           </Label>
//                         </div>
//                       ),
//                     )}
//               </div>
//             </div>
//           </div>
//         )}

//         <div className="md:col-span-2">
//           <Label className="mb-2 block">Jenjang Pendidikan (Checklist)</Label>
//           <div className="p-4 border rounded-lg max-h-60 overflow-y-auto bg-background">
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
//               {isPendingDataJenjangPendidikan
//                 ? Array(6)
//                     .fill(null)
//                     .map((_, i) => <SkeletonRow key={i} />)
//                 : dataJenjangPendidikan?.map((jenjang: JenjangPendidikan) => (
//                     <div
//                       key={jenjang.id_ref_jenjang_pendidikan}
//                       className="flex items-center space-x-2 hover:bg-accent/50 p-1.5 rounded-md transition-colors"
//                     >
//                       <Checkbox
//                         id={`jenjang-${jenjang.id_ref_jenjang_pendidikan}`}
//                         checked={form.jenjang_buka?.some(
//                           (j) =>
//                             j.id_ref_jenjang_pendidikan ===
//                             jenjang.id_ref_jenjang_pendidikan,
//                         )}
//                         onCheckedChange={() => {
//                           setForm((prevForm) => {
//                             const selected = prevForm.jenjang_buka || [];
//                             const isChecked = selected.some(
//                               (j) =>
//                                 j.id_ref_jenjang_pendidikan ==
//                                 jenjang.id_ref_jenjang_pendidikan,
//                             );
//                             const updated = isChecked
//                               ? selected.filter(
//                                   (j) =>
//                                     j.id_ref_jenjang_pendidikan !==
//                                     jenjang.id_ref_jenjang_pendidikan,
//                                 )
//                               : [...selected, jenjang];
//                             return { ...prevForm, jenjang_buka: updated };
//                           });
//                         }}
//                       />
//                       <Label
//                         htmlFor={`jenjang-${jenjang.id_ref_jenjang_pendidikan}`}
//                         className="font-normal cursor-pointer"
//                       >
//                         {jenjang.nama_jenjang_pendidikan}
//                       </Label>
//                     </div>
//                   ))}
//             </div>
//           </div>
//         </div>
//       </FormSection>

//       {/* Action Buttons */}
//       <div className="sticky bottom-0 bg-background/80 backdrop-blur-sm p-4 border-t flex justify-end gap-3 rounded-b-xl z-10">
//         <Button type="button" variant={"outline"} onClick={() => closeModal()}>
//           Batal
//         </Button>
//         <Button
//           className="flex items-center gap-2 min-w-[120px]"
//           disabled={isPending}
//         >
//           {isPending ? (
//             <>
//               <Loader2 className="w-4 h-4 animate-spin" />
//               Processing...
//             </>
//           ) : (
//             "Simpan"
//           )}
//         </Button>
//       </div>
//     </form>
//   );
// }
